// La oficina como la app Casa de Apple (rebrand 03-11-2026, ADR D-037; maquetas web/qa/rebrand-apple/despues/6-7):
// sustituye a la escena en píxeles. Arriba, los widgets de la pared (ventas, cerebros, cuentas, Santi); debajo, cada
// agente como una tarjeta que «se enciende» cuando te necesita. Dos vistas: «Por estado» (los mismos grupos y el mismo
// orden que la lista, con «Con Santi») y «Por sala» (cada sala con su orden de mesas, que Pau cambia con «Ordenar»: la
// misma mecánica POST /salas/:id/mesas que movía a los muñecos). Los agentes de otras personas salen apagados y con su
// dueño, sin avisos (lo que les toca es de su dueño), como en la escena.
import { ChevronRight, Handshake, LayoutGrid, Pin, Plus, Settings2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { OrdenMesas } from '../api';
import type { ContratadoDTO, SalaDTO } from '../compartido/vocabulario';
import { useNoLeido } from '../leidos';
import { bandejaDe, miBandeja } from '../marco/bandejas';
import { hace, quien, ultimoMovimiento } from '../marco/estado';
import { InsigniaSituacion, SiRapido } from '../marco/ListaEquipoS12';
import { agruparPorSituacion, aspectoDe, aspectoGrupo, esperaDesde, fraseDeFila, leToca, seVa, sinNoticiasDesde, tituloGrupo, type Grupo, type SantiDelAgente } from '../marco/situacion';
import { MenuAgente } from './MenuAgente';
import { usePulsacionLarga } from './usePulsacionLarga';
import { useSantiPorAgente } from '../pelotas/porAgente';
import { useOficinaStore } from '../store';
import { BotonIcono, IconoEncargo, Segmentado } from '../ui';

type Vista = 'estado' | 'sala';
const CLAVE_VISTA = 'oficina:vista-casa';

export function Casa({
  salas,
  contratados,
  izquierdaPx,
  resaltado,
  widgets,
  alAbrir,
  alContratar,
  alAjustarSala,
  alOrdenarMesas,
}: {
  salas: SalaDTO[];
  contratados: Record<string, ContratadoDTO>;
  /** Lo que ocupa la lista a la izquierda (con sus márgenes): la Casa empieza ahí. */
  izquierdaPx: number;
  /** La fila de la lista sobre la que está el ratón: su tarjeta se marca. */
  resaltado?: string | null;
  widgets?: ReactNode;
  alAbrir: (agentId: string) => void;
  alContratar: (salaId?: string) => void;
  alAjustarSala?: (salaId: string) => void;
  alOrdenarMesas?: (projectId: string, orden: OrdenMesas) => void;
}) {
  const [vista, setVista] = useState<Vista>(() => (localStorage.getItem(CLAVE_VISTA) === 'sala' ? 'sala' : 'estado'));
  useEffect(() => {
    localStorage.setItem(CLAVE_VISTA, vista);
  }, [vista]);
  const [ordenando, setOrdenando] = useState(false);
  const santi = useSantiPorAgente();
  const mia = miBandeja(useOficinaStore((s) => s.yo));
  const todos = useMemo(() => Object.values(contratados), [contratados]);
  const mios = useMemo(() => todos.filter((c) => bandejaDe(c) === mia), [todos, mia]);
  const ajenos = useMemo(() => todos.filter((c) => bandejaDe(c) !== mia && !seVa(c)), [todos, mia]);

  return (
    <main
      aria-label={t('oficinaV2.titulo')}
      // isolate: nada de dentro (tarjetas, menús) puede pintarse encima del chat ni de la lista.
      className="absolute inset-y-0 right-0 isolate flex flex-col overflow-hidden pr-4 pt-4"
      style={{ left: izquierdaPx }}
    >
      <header className="flex shrink-0 items-center gap-2 pb-3">
        <Segmentado
          etiqueta={t('oficinaV2.casa.vista')}
          opciones={[
            { valor: 'estado', texto: t('oficinaV2.casa.porEstado') },
            { valor: 'sala', texto: t('oficinaV2.casa.porSala') },
          ]}
          valor={vista}
          alCambiar={(v) => {
            setVista(v);
            setOrdenando(false);
          }}
        />
        <span className="flex-1" />
        {vista === 'sala' && alOrdenarMesas ? (
          <button
            type="button"
            onClick={() => setOrdenando((o) => !o)}
            aria-pressed={ordenando}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-o-sub font-semibold outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
              ordenando ? 'bg-oficina-acento text-oficina-acento-tinta hover:bg-oficina-acento-hover' : 'bg-oficina-control text-oficina-texto hover:bg-oficina-pulsado',
            )}
            data-testid="casa-ordenar"
          >
            <LayoutGrid className="h-4 w-4" aria-hidden />
            {ordenando ? t('oficinaV2.casa.listo') : t('oficinaV2.casa.ordenar')}
          </button>
        ) : null}
      </header>

      {widgets ? (
        <section aria-label={t('oficinaV2.casa.widgets')} className="shrink-0 pb-4">
          {widgets}
        </section>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto pb-6 [scrollbar-width:thin]" data-testid="casa">
        {ordenando ? <p className="mb-3 rounded-o-m bg-oficina-acento/10 px-3.5 py-2 text-o-sub text-oficina-te-necesita-claro">{t('oficinaV2.casa.ordenarAviso')}</p> : null}
        {!todos.length ? (
          <div className="mt-16 flex flex-col items-center text-center">
            <h2 className="text-o-titulo3 font-semibold text-oficina-texto">{t('oficinaV2.casa.vacio')}</h2>
            <p className="mt-1.5 max-w-sm text-o-cuerpo text-oficina-texto2">{t('oficinaV2.casa.vacioTexto')}</p>
          </div>
        ) : vista === 'estado' ? (
          <PorEstado mios={mios} ajenos={ajenos} santi={santi} resaltado={resaltado} alAbrir={alAbrir} />
        ) : (
          <PorSala
            salas={salas}
            todos={todos.filter((c) => !seVa(c))}
            mia={mia}
            santi={santi}
            resaltado={resaltado}
            ordenando={ordenando}
            alAbrir={alAbrir}
            alContratar={alContratar}
            alAjustarSala={alAjustarSala}
            alOrdenarMesas={alOrdenarMesas}
          />
        )}
      </div>
    </main>
  );
}

// ── Por estado: los grupos de la lista, en su orden ───────────────────────────────────────────────────────────

function PorEstado({ mios, ajenos, santi, resaltado, alAbrir }: { mios: ContratadoDTO[]; ajenos: ContratadoDTO[]; santi: ReadonlyMap<string, SantiDelAgente>; resaltado?: string | null; alAbrir: (id: string) => void }) {
  const grupos = useMemo(() => agruparPorSituacion(mios, (c) => c.noLeido, santi), [mios, santi]);
  // Los de otras personas, por dueño, al final y apagados.
  const porDueno = useMemo(() => {
    const m = new Map<string, ContratadoDTO[]>();
    for (const c of ajenos) {
      const n = (c.dueno?.nombre ?? 'Pau').split(/\s+/)[0]!;
      m.set(n, [...(m.get(n) ?? []), c]);
    }
    return [...m];
  }, [ajenos]);
  return (
    <>
      {grupos.map(({ grupo, miembros }) => (
        <SeccionGrupo key={grupo} grupo={grupo} n={miembros.length}>
          {miembros.map((c) => (
            <TarjetaAgente key={c.agentId} c={c} santi={santi.get(c.agentId)} resaltada={resaltado === c.agentId} alAbrir={alAbrir} />
          ))}
        </SeccionGrupo>
      ))}
      {porDueno.map(([nombre, lista]) => (
        <section key={nombre} className="mb-5">
          <h2 className="mb-2.5 flex items-center gap-2 px-1 text-o-titulo3 font-semibold text-oficina-texto2">
            {t('oficinaV2.casa.deOtro', { nombre })} <span className="text-o-cuerpo font-semibold text-oficina-texto3">{lista.length}</span>
          </h2>
          <Rejilla>
            {lista.map((c) => (
              <TarjetaAgente key={c.agentId} c={c} ajena={nombre} resaltada={resaltado === c.agentId} alAbrir={alAbrir} />
            ))}
          </Rejilla>
        </section>
      ))}
    </>
  );
}

function SeccionGrupo({ grupo, n, children }: { grupo: Grupo; n: number; children: ReactNode }) {
  const a = aspectoGrupo(grupo);
  const Icono = a.icono;
  // Como en la lista: «Con Santi» y «Se van» van plegados (lo tienen otros / se están yendo: sin ruido).
  const plegable = grupo === 'con_santi' || grupo === 'se_van';
  const [abierto, setAbierto] = useState(!plegable);
  const apagado = grupo === 'en_marcha' || grupo === 'sin_pendiente' || grupo === 'se_van' || grupo === 'problema';
  const titulo = (
    <>
      {Icono ? <Icono className={cn('h-4 w-4 shrink-0', a.texto)} strokeWidth={2.4} aria-hidden /> : null}
      <span className={apagado ? 'text-oficina-texto2' : 'text-oficina-texto'}>{tituloGrupo(grupo)}</span>
      <span className="text-o-cuerpo font-semibold text-oficina-texto3">{n}</span>
    </>
  );
  return (
    <section className="mb-5" data-testid={`casa-grupo-${grupo}`}>
      {plegable ? (
        <button type="button" aria-expanded={abierto} onClick={() => setAbierto((x) => !x)} className="mb-2.5 flex items-center gap-2 rounded-o-s px-1 text-o-titulo3 font-semibold outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
          {titulo}
          <ChevronRight className={cn('h-4 w-4 text-oficina-texto3 transition-transform duration-150 ease-o-apple motion-reduce:transition-none', abierto && 'rotate-90')} aria-hidden />
        </button>
      ) : (
        <h2 className="mb-2.5 flex items-center gap-2 px-1 text-o-titulo3 font-semibold">{titulo}</h2>
      )}
      {abierto ? <Rejilla>{children}</Rejilla> : null}
    </section>
  );
}

function Rejilla({ children }: { children: ReactNode }) {
  return <ul className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2.5">{children}</ul>;
}

// ── Por sala: cada sala con su orden de mesas ─────────────────────────────────────────────────────────────────

function PorSala({
  salas,
  todos,
  mia,
  santi,
  resaltado,
  ordenando,
  alAbrir,
  alContratar,
  alAjustarSala,
  alOrdenarMesas,
}: {
  salas: SalaDTO[];
  todos: ContratadoDTO[];
  mia: string;
  santi: ReadonlyMap<string, SantiDelAgente>;
  resaltado?: string | null;
  ordenando: boolean;
  alAbrir: (id: string) => void;
  alContratar: (salaId?: string) => void;
  alAjustarSala?: (salaId: string) => void;
  alOrdenarMesas?: (projectId: string, orden: OrdenMesas) => void;
}) {
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  return (
    <>
      {[...salas]
        .sort((a, b) => a.orden - b.orden)
        .map((s) => {
          const gente = todos.filter((c) => c.projectId === s.projectId).sort((a, b) => (a.mesa ?? 999) - (b.mesa ?? 999) || a.creadoEn.localeCompare(b.creadoEn));
          const mover = (c: ContratadoDTO, destino: ContratadoDTO | undefined) => {
            if (!destino || destino.agentId === c.agentId || destino.mesa == null || !alOrdenarMesas) return;
            alOrdenarMesas(s.projectId, { accion: 'mover', agentId: c.agentId, mesa: destino.mesa });
          };
          return (
            <section key={s.projectId} className="mb-6" data-testid={`casa-sala-${s.projectId}`}>
              <h2 className="mb-2.5 flex items-center gap-2 px-1">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} aria-hidden />
                <span className="text-o-titulo3 font-semibold text-oficina-texto">{s.nombre}</span>
                <span className="text-o-cuerpo font-semibold text-oficina-texto3">{gente.length}</span>
                <span className="flex-1" />
                <BotonIcono icono={Plus} tamano="sm" etiqueta={t('oficinaV2.casa.contratarAqui', { sala: s.nombre })} onClick={() => alContratar(s.projectId)} />
                {alAjustarSala ? <BotonIcono icono={Settings2} tamano="sm" etiqueta={t('oficinaV2.casa.ajustes')} onClick={() => alAjustarSala(s.projectId)} /> : null}
              </h2>
              <Rejilla>
                {gente.map((c, i) => (
                  <TarjetaAgente
                    key={c.agentId}
                    c={c}
                    santi={santi.get(c.agentId)}
                    ajena={bandejaDe(c) !== mia ? (c.dueno?.nombre ?? 'Pau').split(/\s+/)[0] : undefined}
                    resaltada={resaltado === c.agentId}
                    alAbrir={alAbrir}
                    orden={
                      ordenando && alOrdenarMesas
                        ? {
                            arrastrando: arrastrando === c.agentId,
                            alEmpezar: () => setArrastrando(c.agentId),
                            alSoltar: () => {
                              const origen = gente.find((x) => x.agentId === arrastrando);
                              if (origen) mover(origen, c);
                              setArrastrando(null);
                            },
                            alTerminar: () => setArrastrando(null),
                            alMover: (dir) => mover(c, gente[i + dir]),
                          }
                        : undefined
                    }
                  />
                ))}
              </Rejilla>
            </section>
          );
        })}
      {ordenando && alOrdenarMesas ? (
        <div className="flex flex-wrap gap-2 px-1">
          {salas.map((s) => (
            <button key={s.projectId} type="button" onClick={() => alOrdenarMesas(s.projectId, { accion: 'automatico' })} className="h-8 rounded-full bg-oficina-control px-3.5 text-o-sub font-medium text-oficina-texto2 outline-none hover:bg-oficina-pulsado hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
              {t('oficinaV2.casa.automatico')} · {s.nombre}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}

// ── La tarjeta de un agente ───────────────────────────────────────────────────────────────────────────────────

interface Orden {
  arrastrando: boolean;
  alEmpezar: () => void;
  alSoltar: () => void;
  alTerminar: () => void;
  alMover: (dir: -1 | 1) => void;
}

export function TarjetaAgente({ c, santi, ajena, resaltada, alAbrir, orden }: { c: ContratadoDTO; santi?: SantiDelAgente; ajena?: string; resaltada?: boolean; alAbrir: (id: string) => void; orden?: Orden }) {
  const a = aspectoDe(c);
  // Lo de otra persona no avisa (es de su dueño): apagada, sin «encenderse» ni «Sí».
  const teToca = !ajena && leToca(c);
  const noLeido = useNoLeido(c) && !seVa(c) && !ajena;
  const sinNoticias = sinNoticiasDesde(c);
  const cuando = hace(teToca ? esperaDesde(c) : ultimoMovimiento(c));
  const rapido = !ajena && c.situacion === 'autorizacion' ? c.permisoRapido : null;
  const [menu, setMenu] = useState(false);
  // 09-10 (Pau, iPad): mantener el dedo ~0,5 s abre el menú, como el clic derecho; el clic de levantar el dedo no abre el chat.
  const pulsacion = usePulsacionLarga(() => setMenu(true));
  const ref = useRef<HTMLLIElement>(null);
  return (
    <li
      ref={ref}
      className={cn(
        'relative flex min-h-[124px] flex-col rounded-o-l transition-colors duration-150 ease-o-apple',
        teToca ? 'bg-oficina-acento/[0.12] ring-1 ring-inset ring-oficina-acento/45' : 'bg-oficina-elevada ring-1 ring-inset ring-white/[0.06]',
        ajena && 'opacity-60',
        resaltada && 'ring-2 ring-oficina-texto/60',
        orden?.arrastrando && 'opacity-40',
      )}
      draggable={!!orden}
      onDragStart={orden ? (e) => { e.dataTransfer.effectAllowed = 'move'; orden.alEmpezar(); } : undefined}
      onDragOver={orden ? (e) => e.preventDefault() : undefined}
      onDrop={orden ? (e) => { e.preventDefault(); orden.alSoltar(); } : undefined}
      onDragEnd={orden?.alTerminar}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenu(true);
      }}
      data-testid="tarjeta-agente"
      data-agente={c.agentId}
    >
      <button
        type="button"
        onPointerDown={(e) => pulsacion.empezar(e, c.agentId, !orden)}
        onClick={() => {
          if (pulsacion.consumir()) return;
          alAbrir(c.agentId);
        }}
        onKeyDown={
          orden
            ? (e) => {
                if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                  e.preventDefault();
                  orden.alMover(e.key === 'ArrowLeft' ? -1 : 1);
                }
              }
            : undefined
        }
        aria-label={`${quien(c)}. ${fraseDeFila(c)}${ajena ? `. ${t('oficinaV2.casa.deOtro', { nombre: ajena })}` : ''}`}
        className={cn('flex flex-1 flex-col rounded-o-l p-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70', orden ? 'cursor-grab' : 'hover:bg-white/[0.04]')}
      >
        <span className="flex items-start justify-between gap-2">
          <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={36} />
          <span className={cn('flex items-center gap-1.5', rapido && 'pr-14')}>
            {c.fijado ? <Pin className="h-3.5 w-3.5 text-oficina-texto3" strokeWidth={2.4} aria-hidden /> : null}
            {santi ? <Handshake className="h-4 w-4 text-oficina-santi" strokeWidth={2.2} aria-hidden data-testid="tarjeta-santi" /> : null}
            {noLeido ? <span className="h-2.5 w-2.5 rounded-full bg-oficina-texto" aria-label={t('oficinaV2.s12.sinLeer')} /> : null}
            {ajena ? null : <InsigniaSituacion situacion={c.situacion} c={c} tamano={22} />}
          </span>
        </span>
        <span className={cn('mt-auto line-clamp-2 break-words pt-2 text-o-texto text-oficina-texto', teToca || noLeido ? 'font-semibold' : 'font-medium')}>{quien(c)}</span>
        <span className={cn('mt-0.5 line-clamp-1 text-o-nota', sinNoticias ? 'text-oficina-bloqueado' : teToca ? a.textoFila : 'text-oficina-texto2')}>
          {ajena ? t('oficinaV2.casa.deOtro', { nombre: ajena }) : sinNoticias ?? `${fraseDeFila(c)} · ${cuando}`}
        </span>
      </button>
      {rapido ? (
        // El «Sí» de la lista, arriba a la derecha de la tarjeta (hermano del botón: no abre el chat).
        <span className="absolute right-1 top-2.5">
          <SiRapido c={c} permisoId={rapido.id} />
        </span>
      ) : null}
      {menu ? <MenuAgente c={c} alCerrar={() => setMenu(false)} alAbrir={() => alAbrir(c.agentId)} /> : null}
    </li>
  );
}
